'use strict';

export class ZoomDetector {
    step = 25;
    max = 200;
    bodyObserver = null;
    headerElement = null;
    mainElement = null;
    footerElement = null;
    #bodyObserverInitialized = false;

    constructor() {
        this.bodyObserver = new MutationObserver(this.onBodyObserve);

        this.bind();
    }

    _initDomElements() {
        if (!document.body) {
            return;
        }
        if (!this.headerElement) { // Only find elements once
            [...document.body.children].forEach((el) => {
                if (el.tagName === 'HEADER') {
                    this.headerElement = el;
                } else if (el.tagName === 'MAIN') {
                    this.mainElement = el;
                } else if (el.tagName === 'FOOTER') {
                    this.footerElement = el;
                }
            });
        }
    }

    _initBodyObserver() {
        if (document.body && !this.#bodyObserverInitialized) {
            this.bodyObserver.observe(document.body, {
                attributes: true,
                attributeFilter: ['style']
            });
            this.#bodyObserverInitialized = true;
        }
    }

    bind() {
        const initHandler = () => {
            this._initDomElements();
            this._initBodyObserver();
            this.update();
            document.removeEventListener('DOMContentLoaded', initHandler);
            window.removeEventListener('load', initHandler);
        };

        document.addEventListener('DOMContentLoaded', initHandler);
        window.addEventListener('load', initHandler);

        window.addEventListener('message', this.onWindowMessage);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.onWindowResize);
        } else {
            window.addEventListener('resize', this.onWindowResize);
        }
    }

    getDevicePixelRatio() {
        return window?.devicePixelRatio ?? null;
    }

    getViewportWidth() {
        return window.visualViewport?.width ?? document.body.clientWidth ?? null;
    }

    getOuterWidth() {
        return window.outerWidth;
    }

    getBodyZoom() {
        const zoom = parseFloat(getComputedStyle(document.body).zoom);
        return (isNaN(zoom)) ? 100 : zoom * 100;
    }

    getBrowserZoom() {
        const ratio = (this.getOuterWidth() / this.getViewportWidth()) ?? 1;
        return Math.round(100 * ratio);
    }

    getCombinedZoom() {
        const bodyZoom = this.getBodyZoom();
        const browserZoom = this.getBrowserZoom();

        return Math.round((bodyZoom/100) * (browserZoom/100) * 100);
    }

    update = () => {
        if (!this.headerElement || !this.mainElement || !this.footerElement) {
            this._initDomElements(); // Try to initialize elements again if not set
            if (!this.headerElement || !this.mainElement || !this.footerElement) {
                return; // Cannot proceed without required elements
            }
        }
        const zoom = this.getCombinedZoom();
        const bodyScale = this.getBodyZoom()/100;

        document.documentElement.classList.forEach((thisClass) => {
            if (thisClass.startsWith('min-zoom')) {
                document.documentElement.classList.remove(thisClass);
            }
        });

        for (let i = 0; i <= this.max; i += this.step) {
            if (zoom >= i) {
                document.documentElement.classList.add(`min-zoom-${i}`);
            }
        }

        const calc = `calc(100vw / ${bodyScale})`;
        this.headerElement.style.width = calc; 
        this.mainElement.style.width = calc;
        this.footerElement.style.width = calc;
    }

    onBodyObserve = (mutations) => {
        for (const mutation of mutations) {
            if (mutation.type !== 'attributes' || mutation.attributeName !== 'style') {
                continue;
            }
            
            setTimeout(this.update, 180);
        }
    }

    onWindowMessage = (event) => {
        setTimeout(() => {
            if (!event.acsbListenerEvent) {
                return;
            }
            this.update();
        }, 180);
    }

    onWindowResize = (event) => {
        setTimeout(this.update, 180);
    }
}